// /home/labdoo/zitech-nexus/frontend/src/pages/ResetPassword.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { authAPI } from '../services/api';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.new_password) {
      setError('Please enter a new password');
      return false;
    }
    
    if (formData.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      await authAPI.resetPassword(
        uidb64!,
        token!,
        formData.new_password,
        formData.confirm_password
      );
      setSuccess(true);
    } catch (err: any) {
      console.error('Error:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reset password. The link may have expired.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Navbar />
        <div className="zitech-reset-page">
          <div className="zitech-reset-orb zitech-reset-orb-1"></div>
          <div className="zitech-reset-orb zitech-reset-orb-2"></div>
          <div className="zitech-reset-orb zitech-reset-orb-3"></div>
          
          <div className="zitech-reset-particle zitech-reset-particle-1"></div>
          <div className="zitech-reset-particle zitech-reset-particle-2"></div>
          <div className="zitech-reset-particle zitech-reset-particle-3"></div>
          
          <div className="zitech-reset-container">
            <div className="zitech-reset-card">
              <div className="zitech-reset-header">
                <h1 className="zitech-reset-logo">Password Reset</h1>
              </div>
              
              <div className="zitech-success-message">
                <div className="zitech-success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="10" stroke="#28a745" strokeWidth="2"/>
                  </svg>
                </div>
                <h3>Password Reset Successfully!</h3>
                <p>Your password has been reset. You can now login with your new password.</p>
                <button 
                  className="zitech-back-login-btn" 
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="zitech-reset-page">
        {/* Animated Background Orbs */}
        <div className="zitech-reset-orb zitech-reset-orb-1"></div>
        <div className="zitech-reset-orb zitech-reset-orb-2"></div>
        <div className="zitech-reset-orb zitech-reset-orb-3"></div>
        
        {/* Floating Particles */}
        <div className="zitech-reset-particle zitech-reset-particle-1"></div>
        <div className="zitech-reset-particle zitech-reset-particle-2"></div>
        <div className="zitech-reset-particle zitech-reset-particle-3"></div>
        <div className="zitech-reset-particle zitech-reset-particle-4"></div>
        <div className="zitech-reset-particle zitech-reset-particle-5"></div>
        <div className="zitech-reset-particle zitech-reset-particle-6"></div>
        
        {/* Tech Shapes */}
        <div className="zitech-tech-shape zitech-shape-hex"></div>
        <div className="zitech-tech-shape zitech-shape-triangle"></div>
        <div className="zitech-tech-shape zitech-shape-circle"></div>

        <div className="zitech-reset-container">
          <div className="zitech-reset-card">
            <div className="zitech-reset-header">
              <h1 className="zitech-reset-logo">Reset Password</h1>
              <p className="zitech-reset-subtitle">Enter your new password below</p>
            </div>

            {error && (
              <div className="zitech-reset-error-alert">
                <div className="zitech-reset-error-message">
                  <span className="zitech-reset-error-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </span>
                  {error}
                </div>
              </div>
            )}

            <form className="zitech-reset-form" onSubmit={handleSubmit}>
              <div className="zitech-form-group">
                <label className="zitech-form-label">New Password</label>
                <div className="zitech-input-wrapper">
                  <span className="zitech-input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M18 8C17.5 5 15 3 12 3C8 3 5 5.5 5 9C5 12 6.5 14 8 16L12 21L16 16C17.5 14 19 12 19 9C19 8.5 18.5 8 18 8Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="new_password"
                    className="zitech-form-input"
                    placeholder="Enter new password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="zitech-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                        <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
                <small className="zitech-input-hint">Password must be at least 6 characters</small>
              </div>

              <div className="zitech-form-group">
                <label className="zitech-form-label">Confirm New Password</label>
                <div className="zitech-input-wrapper">
                  <span className="zitech-input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M18 8C17.5 5 15 3 12 3C8 3 5 5.5 5 9C5 12 6.5 14 8 16L12 21L16 16C17.5 14 19 12 19 9C19 8.5 18.5 8 18 8Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirm_password"
                    className="zitech-form-input"
                    placeholder="Confirm new password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button type="submit" className="zitech-reset-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="zitech-spinner"></span>
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            <div className="zitech-reset-footer">
              <button 
                type="button" 
                className="zitech-link-button" 
                onClick={() => navigate('/login')}
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;